import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { format } from 'date-fns';
import {
  ArrowLeft,
  CalendarIcon,
  Plus,
  Check,
  ChevronsUpDown,
  PlusCircle,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NewTicket() {
  const { user, loading: authLoading } = useAuth();
  const { companyId, selectedCompany } = useCompany();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    batch_number: '',
    product_name: '',
    quantity: '',
    unit: 'kg',
    production_date: new Date()
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [productOpen, setProductOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [addingProduct, setAddingProduct] = useState(false);

  // Fetch products from API for the selected company
  const fetchProducts = useCallback(async () => {
    if (!companyId) return;
    
    setProductsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/products?company_id=${companyId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || []);
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setProductsLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (!authLoading && user && companyId) {
      fetchProducts();
    }
  }, [authLoading, user, fetchProducts]);

  // Add custom product to the list
  const handleAddCustomProduct = async (productName) => {
    if (!productName || !productName.trim() || !companyId) return;
    
    setAddingProduct(true);
    try {
      const response = await fetch(`${API_URL}/api/products?company_id=${companyId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: productName.trim() })
      });
      
      if (response.ok) {
        toast.success('Product added to list');
        setFormData({ ...formData, product_name: productName.trim() });
        setProductOpen(false);
        setProductSearch('');
        fetchProducts(); // Refresh the list
      } else {
        const error = await response.json();
        if (error.detail?.includes('already exists')) {
          // Product exists, just select it
          setFormData({ ...formData, product_name: productName.trim() });
          setProductOpen(false);
          setProductSearch('');
        } else {
          toast.error(error.detail || 'Failed to add product');
        }
      }
    } catch (error) {
      console.error('Failed to add product:', error);
      // Still allow using the custom name even if save fails
      setFormData({ ...formData, product_name: productName.trim() });
      setProductOpen(false);
      setProductSearch('');
    } finally {
      setAddingProduct(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.batch_number.trim()) newErrors.batch_number = 'Batch number is required';
    if (!formData.product_name.trim()) newErrors.product_name = 'Product name is required';
    if (!formData.quantity || parseFloat(formData.quantity) <= 0) newErrors.quantity = 'Valid quantity is required';
    if (!formData.unit.trim()) newErrors.unit = 'Unit is required';
    if (!formData.production_date) newErrors.production_date = 'Production date is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    if (!companyId) {
      toast.error('Please select a company first');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          company_id: companyId,
          quantity: parseFloat(formData.quantity),
          production_date: format(formData.production_date, 'yyyy-MM-dd')
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Ticket created successfully');
        navigate(`/tickets/${data.ticket_id}`);
      } else if (response.status === 401) {
        toast.error('Session expired. Please log out and log back in.');
      } else if (response.status === 403) {
        toast.error('You do not have permission to create tickets. Please contact your manager.');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create ticket');
      }
    } catch (error) {
      console.error('Failed to create ticket:', error);
      toast.error('Failed to create ticket');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <Layout>
        <div className="loading-state">
          <div className="loading-spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-content" data-testid="new-ticket-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/tickets')}
            className="h-10 w-10 p-0"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">New Production Entry</h1>
            <p className="text-slate-500 mt-1">Create a new production batch ticket</p>
          </div>
        </div>

        {/* Form */}
        <div className="max-w-2xl">
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Batch Number */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Batch Number <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={formData.batch_number}
                    onChange={(e) => setFormData({ ...formData, batch_number: e.target.value })}
                    placeholder="e.g., BATCH-2024-001"
                    className={`h-12 ${errors.batch_number ? 'border-red-500' : ''}`}
                    data-testid="batch-number-input"
                  />
                  {errors.batch_number && (
                    <p className="text-xs text-red-500 mt-1">{errors.batch_number}</p>
                  )}
                </div>

                {/* Product Name - Searchable Dropdown */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Product Name <span className="text-red-500">*</span>
                  </label>
                  <Popover open={productOpen} onOpenChange={setProductOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={productOpen}
                        disabled={productsLoading}
                        className={cn(
                          "h-12 w-full justify-between font-normal",
                          !formData.product_name && "text-muted-foreground",
                          errors.product_name && "border-red-500"
                        )}
                        data-testid="product-name-input"
                      >
                        {productsLoading ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Loading...
                          </span>
                        ) : (
                          formData.product_name || "Select product..."
                        )}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0" align="start">
                      <Command shouldFilter={true}>
                        <CommandInput 
                          placeholder="Search products..." 
                          value={productSearch}
                          onValueChange={setProductSearch}
                        />
                        <CommandList>
                          <CommandEmpty>
                            <div className="py-2 text-center text-sm text-slate-500">
                              No product found.
                            </div>
                          </CommandEmpty>
                          <CommandGroup>
                            {products.map((product) => (
                              <CommandItem
                                key={product.product_id}
                                value={product.name}
                                onSelect={(currentValue) => {
                                  setFormData({ ...formData, product_name: currentValue });
                                  setProductOpen(false);
                                  setProductSearch('');
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    formData.product_name === product.name ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                {product.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                          {/* Add Custom Item Option */}
                          <div className="border-t border-slate-200 p-2">
                            <button
                              type="button"
                              disabled={addingProduct}
                              className="w-full flex items-center gap-2 px-2 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md transition-colors disabled:opacity-50"
                              onClick={() => {
                                const customName = productSearch.trim() || prompt('Enter custom product name:');
                                if (customName && customName.trim()) {
                                  handleAddCustomProduct(customName.trim());
                                }
                              }}
                              data-testid="add-custom-product-btn"
                            >
                              {addingProduct ? (
                                <Loader2 className="w-4 h-4 animate-spin text-green-600" />
                              ) : (
                                <PlusCircle className="w-4 h-4 text-green-600" />
                              )}
                              <span>
                                {productSearch.trim() 
                                  ? `Add "${productSearch.trim()}"` 
                                  : "Add custom product"}
                              </span>
                            </button>
                          </div>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  {errors.product_name && (
                    <p className="text-xs text-red-500 mt-1">{errors.product_name}</p>
                  )}
                </div>

                {/* Quantity */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Quantity <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    placeholder="e.g., 100"
                    className={`h-12 ${errors.quantity ? 'border-red-500' : ''}`}
                    data-testid="quantity-input"
                  />
                  {errors.quantity && (
                    <p className="text-xs text-red-500 mt-1">{errors.quantity}</p>
                  )}
                </div>

                {/* Unit */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Unit <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    placeholder="e.g., kg, pcs, liters"
                    className={`h-12 ${errors.unit ? 'border-red-500' : ''}`}
                    data-testid="unit-input"
                  />
                  {errors.unit && (
                    <p className="text-xs text-red-500 mt-1">{errors.unit}</p>
                  )}
                </div>

                {/* Production Date */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Production Date <span className="text-red-500">*</span>
                  </label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={`h-12 w-full justify-start text-left font-normal ${errors.production_date ? 'border-red-500' : ''}`}
                        data-testid="production-date-picker"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.production_date ? format(formData.production_date, 'PPP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.production_date}
                        onSelect={(date) => setFormData({ ...formData, production_date: date })}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  {errors.production_date && (
                    <p className="text-xs text-red-500 mt-1">{errors.production_date}</p>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex items-center gap-4 pt-4 border-t border-slate-200">
                <Button
                  type="submit"
                  disabled={loading}
                  className="h-12 px-8 bg-slate-900 hover:bg-slate-800"
                  data-testid="submit-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {loading ? 'Creating...' : 'Create Ticket'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/tickets')}
                  className="h-12"
                  data-testid="cancel-btn"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
}
